import { FaSpinner } from 'react-icons/fa';
import { Badge, Button, ToggleSwitch } from 'components/common';
import PropTypes from 'prop-types';
import AssignRole from "./assign/AssignRole";
import UserRoleList from './UserRoleList';
import RecordLevelSalesUnit from "./assign/RecordLevelSalesUnit";
import RecordLevelCarBookingSite from "./assign/RecordLevelCarBookingSite";
import RecordLevelTravelBookingSite from "./assign/RecordLevelTravelBookingSite";
import RecordLevelTravelBookingSubmitter from "./assign/RecordLevelTravelBookingSubmitter";
import React, { useEffect, useMemo, memo, useState } from "react";
import { getActiveSites } from "redux/organization/siteSlice";
import { useSelector, useDispatch } from "react-redux";
import { FaUserPlus, FaUserShield, FaCar, FaPlane, FaStore, FaUserFriends } from "react-icons/fa";
import { BsShieldCheck } from "react-icons/bs";
import { MdInventory, MdMeetingRoom } from "react-icons/md";
import RecordLevelErpWarehouse from "./assign/RecordLevelErpWarehouse";
import { updateMeetingRoomAdmin } from 'redux/authorization/userSlice';
import { toast } from 'react-toastify';
import { LuMerge } from 'react-icons/lu';
import CombineRolesDrawer from 'components/app/authorization/role/CombineRolesDrawer';
import { getUser } from 'redux/authorization/userSlice';
// Icon-chip accent per variant (design-system tw- utilities, no inline hex).
const ACCENTS = {
  primary: 'tw-bg-blue-50 tw-text-blue-600',
  success: 'tw-bg-emerald-50 tw-text-emerald-600',
  info:    'tw-bg-cyan-50 tw-text-cyan-600',
  warning: 'tw-bg-amber-50 tw-text-amber-600',
  violet:  'tw-bg-violet-50 tw-text-violet-600',
};
const BADGE_VARIANT = { primary: 'primary', success: 'success', info: 'info', warning: 'warning', violet: 'primary' };

// Memoized SectionCard — consistent card chrome with an icon chip, title/subtitle and
// either a count Badge or a custom header-right slot.
const SectionCard = memo(({ icon: Icon, title, subtitle, badge, variant = 'primary', headerRight, children }) => (
  <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-bg-white tw-flex tw-flex-col tw-h-full">
    <div className="tw-px-4 tw-py-3 tw-border-b tw-border-slate-100 tw-flex tw-items-center tw-justify-between tw-gap-3">
      <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
        <div className={`tw-w-9 tw-h-9 tw-rounded tw-flex tw-items-center tw-justify-center tw-shrink-0 ${ACCENTS[variant] || ACCENTS.primary}`}>
          <Icon size={16} />
        </div>
        <div className="tw-min-w-0">
          <h3 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-slate-800 tw-truncate">{title}</h3>
          {subtitle && <p className="tw-mb-0 tw-text-xs tw-text-slate-400 tw-truncate">{subtitle}</p>}
        </div>
      </div>
      {headerRight !== undefined
        ? headerRight
        : (badge !== undefined && <Badge variant={BADGE_VARIANT[variant] || 'primary'} size="sm">{badge}</Badge>)}
    </div>
    <div className="tw-p-4 tw-flex-1">{children}</div>
  </div>
));

SectionCard.displayName = 'SectionCard';

SectionCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  badge: PropTypes.number,
  variant: PropTypes.string,
  headerRight: PropTypes.node,
  children: PropTypes.node.isRequired,
};

// Accordion row header — tw-border-0 is required: Tailwind preflight is disabled, so
// a bare <button> would otherwise show the browser's default black border.
const AccordionRow = ({ open, onClick, icon: Icon, iconClass, label, count, countClass }) => (
  <button
    type="button"
    onClick={onClick}
    className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-text-left tw-border-0 tw-cursor-pointer tw-transition-colors ${open ? 'tw-bg-slate-50' : 'tw-bg-white hover:tw-bg-slate-50'}`}
  >
    <div className="tw-flex tw-items-center tw-gap-2">
      <Icon className={iconClass} size={14} />
      <span className="tw-text-sm tw-font-semibold tw-text-slate-700">{label}</span>
    </div>
    <span className={`tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${countClass}`}>{count}</span>
  </button>
);
AccordionRow.propTypes = {
  open: PropTypes.bool, onClick: PropTypes.func, icon: PropTypes.elementType,
  iconClass: PropTypes.string, label: PropTypes.string, count: PropTypes.number, countClass: PropTypes.string,
};

const BookingSitesCard = memo(({ counts, userId, record_level_car_booking_sites, record_level_travel_booking_sites, record_level_travel_booking_submitters, carSites, travelSites }) => {
  const [openPanel, setOpenPanel] = useState('car');
  return (
    <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-bg-white tw-flex tw-flex-col tw-h-full">
      <div className="tw-px-4 tw-py-3 tw-border-b tw-border-slate-100 tw-flex tw-items-center tw-gap-3">
        <div className="tw-w-9 tw-h-9 tw-rounded tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-bg-cyan-50 tw-text-cyan-600">
          <FaUserPlus size={16} />
        </div>
        <div className="tw-min-w-0">
          <h3 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-slate-800">Booking Site Access</h3>
          <p className="tw-mb-0 tw-text-xs tw-text-slate-400">Manage site-level permissions</p>
        </div>
      </div>

      <div className="tw-divide-y tw-divide-slate-100 tw-flex-1">
        <div>
          <AccordionRow open={openPanel === 'car'} onClick={() => setOpenPanel(openPanel === 'car' ? null : 'car')}
            icon={FaCar} iconClass="tw-text-blue-600" label="Car Booking Sites"
            count={counts.carSites} countClass="tw-bg-blue-100 tw-text-blue-700" />
          {openPanel === 'car' && (
            <div className="tw-p-4 tw-bg-slate-50/60">
              <RecordLevelCarBookingSite userId={userId} data={record_level_car_booking_sites} sites={carSites} />
            </div>
          )}
        </div>
        <div>
          <AccordionRow open={openPanel === 'travel'} onClick={() => setOpenPanel(openPanel === 'travel' ? null : 'travel')}
            icon={FaPlane} iconClass="tw-text-cyan-600" label="Travel Booking Sites"
            count={counts.travelSites} countClass="tw-bg-cyan-100 tw-text-cyan-700" />
          {openPanel === 'travel' && (
            <div className="tw-p-4 tw-bg-slate-50/60">
              <RecordLevelTravelBookingSite userId={userId} data={record_level_travel_booking_sites} sites={travelSites} />
            </div>
          )}
        </div>
        <div>
          <AccordionRow open={openPanel === 'submitters'} onClick={() => setOpenPanel(openPanel === 'submitters' ? null : 'submitters')}
            icon={FaUserFriends} iconClass="tw-text-cyan-600" label="Travel Booking Submitters"
            count={counts.travelSubmitters} countClass="tw-bg-cyan-100 tw-text-cyan-700" />
          {openPanel === 'submitters' && (
            <div className="tw-p-4 tw-bg-slate-50/60">
              <RecordLevelTravelBookingSubmitter userId={userId} data={record_level_travel_booking_submitters || []} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

BookingSitesCard.displayName = 'BookingSitesCard';

BookingSitesCard.propTypes = {
  counts: PropTypes.object.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  record_level_car_booking_sites: PropTypes.array,
  record_level_travel_booking_sites: PropTypes.array,
  record_level_travel_booking_submitters: PropTypes.array,
  carSites: PropTypes.array,
  travelSites: PropTypes.array,
};

const UserDetailAuthorization = ({ user }) => {
  const { activeData: sites } = useSelector(state => state.organization_site);

  const dispatch = useDispatch();
  const [combineOpen, setCombineOpen] = useState(false);
  const [isUpdatingMeetingRoom, setIsUpdatingMeetingRoom] = useState(false);
  const [localMeetingRoomAdmin, setLocalMeetingRoomAdmin] = useState(user?.admin_meeting_room);



  const {
    roles,
    record_level_sales_units,
    record_level_car_booking_sites,
    record_level_travel_booking_sites,
    record_level_travel_booking_submitters,
    record_level_erp_warehouses,
    id: userId,
    name: userName
  } = user || {};

  // Memoize filtered sites to prevent recalculation
  const travelSites = useMemo(() => 
    sites?.filter(item => item.travel_booking === true) || [],
    [sites]
  );

  const carSites = useMemo(() => 
    sites?.filter(item => item.car_booking === true) || [],
    [sites]
  );

  // Memoize counts
  const counts = useMemo(() => ({
    roles: roles?.length || 0,
    salesUnits: record_level_sales_units?.length || 0,
    carSites: record_level_car_booking_sites?.length || 0,
    travelSites: record_level_travel_booking_sites?.length || 0,
    travelSubmitters: record_level_travel_booking_submitters?.length || 0,
    erpWarehouses: record_level_erp_warehouses?.length || 0
  }), [roles, record_level_sales_units, record_level_car_booking_sites, record_level_travel_booking_sites, record_level_travel_booking_submitters, record_level_erp_warehouses]);

  useEffect(() => {
    dispatch(getActiveSites());
  }, [dispatch]);

  useEffect(() => {
    setLocalMeetingRoomAdmin(user?.admin_meeting_room);
  }, [user]);

  const handleUpdateMeetingRoomAdmin = async () => {
    try {
      setIsUpdatingMeetingRoom(true);
      // Optimistic update
      setLocalMeetingRoomAdmin(!localMeetingRoomAdmin);
      await dispatch(updateMeetingRoomAdmin({ userId, data: { admin_meeting_room: !localMeetingRoomAdmin } })).unwrap();
    } catch (error) {
      // Revert on error
      setLocalMeetingRoomAdmin(!localMeetingRoomAdmin);
      toast.error(error?.message || 'Failed to update meeting room admin status');
    } finally {
      setIsUpdatingMeetingRoom(false);
    }
  };


  const summaryStats = [
    { Icon: FaUserShield, cls: 'tw-text-blue-600',    value: counts.roles,         label: 'Roles' },
    { Icon: FaStore,      cls: 'tw-text-emerald-600', value: counts.salesUnits,    label: 'Sales Units' },
    { Icon: FaCar,        cls: 'tw-text-blue-600',    value: counts.carSites,      label: 'Car Sites' },
    { Icon: FaPlane,      cls: 'tw-text-cyan-600',    value: counts.travelSites,   label: 'Travel Sites' },
    { Icon: MdInventory,  cls: 'tw-text-amber-600',   value: counts.erpWarehouses, label: 'Warehouses' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-5">
        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-bg-blue-50 tw-text-blue-600">
          <BsShieldCheck size={20} />
        </div>
        <div className="tw-min-w-0">
          <h2 className="tw-mb-0 tw-text-lg tw-font-bold tw-text-slate-900">User Authorization</h2>
          <p className="tw-mb-0 tw-text-sm tw-text-slate-500">
            Manage roles, record-level access and site permissions for {userName || 'this user'}
          </p>
        </div>
      </div>

      {/* Summary stat tiles */}
      <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3 tw-mb-5">
        {summaryStats.map((s) => (
          <div key={s.label} className="tw-flex tw-flex-col tw-items-center tw-py-4 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
            <s.Icon className={s.cls} size={18} />
            <div className="tw-text-xl tw-font-bold tw-text-slate-800 tw-mt-1.5 tw-leading-none">{s.value}</div>
            <div className="tw-text-xs tw-text-slate-500 tw-mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Roles & Permissions — primary, full width */}
      <SectionCard
        icon={FaUserShield}
        title="Roles & Permissions"
        subtitle="System roles assigned to this user"
        variant="primary"
        headerRight={
          <div className="tw-flex tw-items-center tw-gap-2">
            {/* Gộp role chồng chéo: xoá role nguồn khỏi TOÀN hệ thống, nên chỉ
                mở khi user có từ 2 role trở lên. */}
            {roles.length > 1 && (
              <Button type="button" variant="ghost" size="sm" leftIcon={LuMerge} onClick={() => setCombineOpen(true)}>
                Combine
              </Button>
            )}
            <Badge variant="primary" size="sm">{counts.roles}</Badge>
          </div>
        }
      >
        <UserRoleList roles={roles} />
        <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-slate-100">
          <AssignRole userId={userId} data={roles} />
        </div>
      </SectionCard>

      <CombineRolesDrawer
        open={combineOpen}
        setOpen={setCombineOpen}
        roles={roles}
        onCombined={() => dispatch(getUser(userId))}
      />

      {/* Record-level access — balanced 2-column grid */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4 tw-mt-4 tw-items-start">
        <SectionCard
          icon={FaStore}
          title="Sales Unit Access"
          subtitle="Limit CRM record access by sales unit"
          badge={counts.salesUnits}
          variant="success"
        >
          <RecordLevelSalesUnit userId={userId} data={record_level_sales_units} />
        </SectionCard>

        <SectionCard
          icon={MdInventory}
          title="ERP Inventory Access"
          subtitle="Warehouse-level ERP access"
          badge={counts.erpWarehouses}
          variant="warning"
        >
          <RecordLevelErpWarehouse userId={userId} data={record_level_erp_warehouses} />
        </SectionCard>

        <SectionCard
          icon={MdMeetingRoom}
          title="Admin Meeting Room Access"
          subtitle="Manage meeting room bookings for all users"
          variant="violet"
          headerRight={<Badge variant={localMeetingRoomAdmin ? 'success' : 'default'} size="sm">{localMeetingRoomAdmin ? 'On' : 'Off'}</Badge>}
        >
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-sm tw-text-slate-600">Meeting Room Admin</span>
            <div className="tw-flex tw-items-center tw-gap-2">
              {isUpdatingMeetingRoom && <FaSpinner className="tw-animate-spin tw-text-blue-500" size={14} />}
              <ToggleSwitch
                value={!!localMeetingRoomAdmin}
                onChange={handleUpdateMeetingRoomAdmin}
                disabled={isUpdatingMeetingRoom}
                size="sm"
              />
            </div>
          </div>
        </SectionCard>

        <BookingSitesCard
          counts={counts}
          userId={userId}
          record_level_car_booking_sites={record_level_car_booking_sites}
          record_level_travel_booking_sites={record_level_travel_booking_sites}
          record_level_travel_booking_submitters={record_level_travel_booking_submitters}
          carSites={carSites}
          travelSites={travelSites}
        />
      </div>
    </div>
  );
};

UserDetailAuthorization.propTypes = {
  user: PropTypes.object.isRequired
};

export default memo(UserDetailAuthorization);